import React, { useContext } from "react";
import _ from "lodash";
import DraftStateContext from "../../contexts/draftStateContext";
import useDraftOnClock from "../../hooks/queryResults/draft/useDraftOnClock";
import useDraftPicks from "../../hooks/queryResults/draft/useDraftPicks";
import useDraftSettings from "../../hooks/queryResults/draft/useDraftSettings";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useTeams from "../../hooks/queryResults/useTeams";
import useCurrentUser from "../../hooks/useCurrentUser";
import Util from "../../util/util";
import PlayerImageByPos from "../player/PlayerImageByPos";
import Loading from "../ui/Loading";
import styled from "styled-components";
import TeamLogoWithRosterLink from "./TeamLogoWithRosterLink";

// const color = "#ffabab";

const StyledContainerDiv = styled.div``;

const StyledTable = styled.table`
  width: 100%;

  & td,
  & th {
    padding: 0;
  }

  & td.qb {
    background-color: #ffabab;
  }

  & td.rb {
    background-color: #abffab;
  }

  & td.wr {
    background-color: #ababff;
  }

  & td.te {
    background-color: #abffff;
  }

  & td.k {
    background-color: #ffabff;
  }

  & td.def {
    background-color: #ffffab;
  }

  & td.on-clock {
    background-color: #0078f8;
    color: white;
  }

  & td.is-user {
    color: white;
    background-color: #00ab00;
  }

  & td.danger {
    color: white;
    background-color: #ff0000;
  }

  &.positions {
    width: 15rem;
    margin: 0.5rem auto;

    & td {
      width: 2rem;
      height: 2rem;
    }
  }
`;

export default function Grid() {
  const draftState = useContext(DraftStateContext);
  const { draftPicksData, draftPicksQuery } = useDraftPicks();
  const { draftSettingsData, draftSettingsQuery } = useDraftSettings();
  const { playersData, playersQuery } = useAllPlayers();
  const { teams, teamsQuery } = useTeams();
  const { formatted: onClock } = useDraftOnClock();
  const currentUser = useCurrentUser();

  const isLoading = useIsLoading(
    draftPicksData,
    draftPicksQuery,
    draftSettingsData,
    draftSettingsQuery,
    playersData,
    playersQuery,
    teams,
    teamsQuery
  );

  if (isLoading) return <Loading />;

  const userHasDraftManagerRole = Util.hasDraftManagerRole(
    currentUser,
    teams.getTeamById(draftState.info.teamIdOnClock, false)
  );

  return (
    <StyledContainerDiv>
      <StyledTable>
        <thead>
          <tr>
            <th title={"Round"}>Rd</th>
            {draftPicksData.picks
              .slice(0, draftSettingsData.settings.numTeams)
              .map((pick, i) => {
                const teamId = pick.team;
                let teamLogo = null;
                if (teamId) {
                  const team = teams.getTeamById(teamId);
                  if (team)
                    teamLogo = (
                      <TeamLogoWithRosterLink team={team} size={"sm"} />
                    );
                }

                return (
                  <th key={i}>
                    {i + 1}
                    {teamLogo}
                  </th>
                );
              })}
          </tr>
        </thead>
        <tbody>
          {_.range(1, draftSettingsData.settings.numRounds + 1).map((round) => {
            const { numTeams } = draftSettingsData.settings;
            const start = (round - 1) * numTeams;
            const end = start + numTeams;
            const picks = draftPicksData.picks.slice(start, end);
            if (round % 2 === 0) picks.reverse();
            return (
              <tr key={round} style={{ height: "36px" }}>
                <td>{round}</td>
                {picks.map((pick) => {
                  let className = null;
                  let cellContent = null;
                  if (pick.player) {
                    const player = playersData.players[pick.player];
                    cellContent = (
                      <PlayerImageByPos player={player} isAnimated={false} />
                    );
                    className = player.pos.toLowerCase();
                  } else if (
                    pick.pickNum === draftState.info.pickNumOnClock &&
                    draftState.info.hasStarted &&
                    !draftState.info.isChoosingSpots
                  ) {
                    cellContent = onClock;
                    className = "on-clock";
                    if (userHasDraftManagerRole) {
                      className = draftState.info.timeIsRunningOut
                        ? "danger"
                        : "is-user";
                    }
                  }

                  return (
                    <td key={pick.pickNum} className={className}>
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </StyledTable>
      <StyledTable className={"positions no-stripes no-hover"}>
        <tbody>
          <tr>
            <td>Key:</td>
            <td className={"qb"}>QB</td>
            <td className={"rb"}>RB</td>
            <td className={"wr"}>WR</td>
            <td className={"te"}>TE</td>
            <td className={"k"}>K</td>
            <td className={"def"}>DEF</td>
          </tr>
        </tbody>
      </StyledTable>
    </StyledContainerDiv>
  );
}
